import dayjs from 'dayjs';
import { type TypeWindowKey, windowRange } from '@/function/calendar/calendar.window';
import { normalizeEvent } from '@/function/calendar/calendar.event.normalize';
import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import { dropEvent, writeEvent } from '@/store/api/calendar/calendar.event.cache';
import type { TypeEvent, TypeEventSnoozed, TypeEventStatus } from '@/types/calendar/types.calendar';
import type { TypeEventInput, TypeEventPatch, TypeOccurrenceChange } from '@/types/calendar/types.calendar.input';
import eventPatchBody from '@/store/api/calendar/calendar.event.patch.body';
import type { TypeSeriesEnd, TypeSeriesSplit } from '@/types/calendar/types.calendar.series';

const PAGE_SIZE = 200;

const MAX_PAGES = 40;

const KEEP_FOR = 900;

const readAll = async (params: Record<string, unknown>) => {
    const outcome = await walkPages<TypeEvent>({
        url: '/events', fallback: 'Non è stato possibile caricare il calendario', params, pageSize: PAGE_SIZE, maxPages: MAX_PAGES,
    });
    return 'data' in outcome ? { data: outcome.data.map(normalizeEvent) } : outcome;
};

const body = (event: TypeEventInput) => ({
    title: event.title,
    kind: event.kind,
    color: event.color ?? '',
    description: event.description ?? '',
    startAt: event.startAt,
    endAt: event.endAt,
    allDay: event.allDay ?? false,
    projectId: event.projectId ?? '',
    taskId: event.taskId ?? '',
    attendeeIds: event.attendeeIds ?? [],
    place: event.place ?? '',
    alerts: event.alerts ?? [],
    priority: event.priority ?? 'low',
    recurrence: event.recurrence ?? null,
    references: (event.references ?? []).map(({ kind, id, title }) => ({ kind, id, title })),
});


export const eventApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listSeries: build.query<TypeEvent[], void>({
            queryFn: () => readAll({ repeats: 'series' }),
            providesTags: ['Event'],
            keepUnusedDataFor: KEEP_FOR,
        }),

        listWindow: build.query<TypeEvent[], TypeWindowKey>({
            queryFn: (key) => {
                const range = windowRange(key);
                return readAll({ repeats: 'single', from: range.from.toISOString(), to: range.to.toISOString() });
            },
            providesTags: ['Event'],
            keepUnusedDataFor: KEEP_FOR,
        }),

        listOpenReminders: build.query<TypeEvent[], string>({
            queryFn: (day) => readAll({ kind: 'reminder', status: 'planned', repeats: 'single', to: dayjs(day).endOf('day').toISOString() }),
            providesTags: ['Event'],
            keepUnusedDataFor: KEEP_FOR,
        }),

        listLinkedEvents: build.query<TypeEvent[], { reference: string; from: string }>({
            queryFn: ({ reference, from }) => readAll({ reference, from: dayjs(from).startOf('day').toISOString() }),
            providesTags: ['Event'],
        }),

        listAllEvents: build.query<TypeEvent[], void>({
            queryFn: () => readAll({}),
            providesTags: ['Event'],
        }),

        createEvent: build.mutation<TypeEvent, TypeEventInput>({
            query: (event) => ({ url: '/events', method: 'post', data: { event: body(event) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                }
            },
        }),

        updateEvent: build.mutation<TypeEvent, { id: string; event: TypeEventPatch }>({
            query: ({ id, event }) => ({ url: `/events/${id}`, method: 'patch', data: { event: eventPatchBody(event) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                }
            },
        }),

        moveEvent: build.mutation<TypeEvent, { id: string; startAt: string; endAt: string; allDay?: boolean }>({
            query: ({ id, startAt, endAt, allDay }) => ({
                url: `/events/${id}/move`,
                method: 'patch',
                data: { startAt, endAt, ...(allDay === undefined ? {} : { allDay }) },
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                }
            },
        }),

        snoozeEvent: build.mutation<TypeEventSnoozed, { id: string; startAt: string; endAt: string; date?: string }>({
            query: ({ id, startAt, endAt, date }) => ({
                url: `/events/${id}/snooze`,
                method: 'patch',
                data: { startAt, endAt, date: date ?? '' },
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.master) writeEvent(dispatch, data.master);
                    writeEvent(dispatch, data.event);
                } catch {
                }
            },
        }),

        unsnoozeEvent: build.mutation<TypeEvent, string>({
            query: (id) => ({ url: `/events/${id}/snooze`, method: 'delete' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                }
            },
        }),

        setEventStatus: build.mutation<TypeEvent, { id: string; status: TypeEventStatus; before?: TypeEvent }>({
            query: ({ id, status }) => ({ url: `/events/${id}/status`, method: 'patch', data: { status } }),
            onQueryStarted: async ({ status, before }, { dispatch, queryFulfilled }) => {
                if (before) writeEvent(dispatch, { ...before, status });
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                    if (before) writeEvent(dispatch, before);
                }
            },
        }),

        removeEvent: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/events/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropEvent(dispatch, id);
                } catch {
                }
            },
        }),

        skipOccurrence: build.mutation<TypeEvent, { id: string; date: string }>({
            query: ({ id, date }) => ({ url: `/events/${id}/occurrence`, method: 'delete', params: { date } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data);
                } catch {
                }
            },
        }),

        endSeries: build.mutation<TypeSeriesEnd, { id: string; from: string; at: string }>({
            query: ({ id, from, at }) => ({ url: `/events/${id}/series`, method: 'delete', params: { from, at } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    if (data.removed) dropEvent(dispatch, data.id);
                    else if (data.event) writeEvent(dispatch, data.event);
                } catch {
                }
            },
        }),

        detachOccurrence: build.mutation<
            TypeSeriesSplit,
            { id: string; date: string; startAt: string; endAt: string; allDay?: boolean; event?: TypeOccurrenceChange }
        >({
            query: ({ id, date, startAt, endAt, allDay, event }) => ({
                url: `/events/${id}/occurrence`,
                method: 'post',
                data: { date, startAt, endAt, ...(allDay === undefined ? {} : { allDay }), ...(event ? { event: eventPatchBody(event) } : {}) },
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeEvent(dispatch, data.master);
                    writeEvent(dispatch, data.event);
                } catch {
                }
            },
        }),

        splitSeries: build.mutation<
            TypeSeriesSplit,
            { id: string; date: string; at: string; startAt: string; endAt: string; allDay?: boolean; event?: TypeOccurrenceChange }
        >({
            query: ({ id, date, at, startAt, endAt, allDay, event }) => ({
                url: `/events/${id}/series`,
                method: 'post',
                data: { date, at, startAt, endAt, ...(allDay === undefined ? {} : { allDay }), ...(event ? { event: eventPatchBody(event) } : {}) },
            }),
            onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;

                    if (data.replaced) dropEvent(dispatch, id);
                    else writeEvent(dispatch, data.master);

                    writeEvent(dispatch, data.event);
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListSeriesQuery, useListWindowQuery, useLazyListAllEventsQuery, useListOpenRemindersQuery, useListLinkedEventsQuery,
} = eventApi;

export default eventApi;
